import { gpx, kml, tcx } from '@tmcw/togeojson';
import { haversine, measureTrack, simplifyForStorage } from './geo';
import { cleanCoords, uid, type Route } from './schema';

export class RouteFileError extends Error {}

const baseName = (file: string) => file.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();

/** Moving time in seconds: time between consecutive points, ignoring stops and recording gaps. */
function movingTime(coords: number[][], times: unknown): number | null {
  if (!Array.isArray(times) || times.length !== coords.length) return null;
  let total = 0;
  let counted = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = Date.parse(times[i - 1]);
    const b = Date.parse(times[i]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
    const dt = (b - a) / 1000;
    if (dt <= 0 || dt > 300) continue; // paused or lost signal
    if (haversine(coords[i - 1] as [number, number], coords[i] as [number, number]) / dt < 0.3) continue; // standing still
    total += dt;
    counted++;
  }
  return counted ? total : null;
}

/** Parse a GPX, TCX or KML document into routes (one per track). */
export function parseRouteFile(text: string, fileName: string): Route[] {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.querySelector('parsererror')) throw new RouteFileError(`${fileName} is not a valid route file.`);
  const root = doc.documentElement.nodeName.toLowerCase();
  const fc = root === 'kml' ? kml(doc) : root === 'trainingcenterdatabase' ? tcx(doc) : gpx(doc);

  const routes: Route[] = [];
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    const lines: number[][][] =
      g.type === 'LineString' ? [g.coordinates] : g.type === 'MultiLineString' ? g.coordinates : [];
    const coords = lines.flat();
    const full = cleanCoords(coords);
    const measure = measureTrack(full);
    // Devices often leave stub tracks behind (a couple of points where recording was toggled).
    if (full.length < 3 || measure.distance < 50) continue;
    const rawTimes = (f.properties as any)?.coordinateProperties?.times;
    const times = Array.isArray(rawTimes) ? (g.type === 'MultiLineString' ? rawTimes.flat() : rawTimes) : null;
    routes.push({
      id: uid(),
      name: (typeof f.properties?.name === 'string' && f.properties.name.trim()) || baseName(fileName),
      source: 'gpx',
      coords: simplifyForStorage(full),
      movingTime: movingTime(coords, times),
      ...measure,
    });
  }
  if (!routes.length) throw new RouteFileError(`${fileName} doesn't contain a recorded track.`);
  return routes;
}

export async function readRouteFiles(files: Iterable<File>): Promise<{ routes: Route[]; errors: string[] }> {
  const routes: Route[] = [];
  const errors: string[] = [];
  for (const file of files) {
    if (!/\.(gpx|tcx|kml)$/i.test(file.name)) {
      errors.push(`${file.name}: only GPX, TCX and KML files are supported.`);
      continue;
    }
    try {
      routes.push(...parseRouteFile(await file.text(), file.name));
    } catch (e) {
      errors.push(e instanceof RouteFileError ? e.message : `${file.name} could not be read.`);
    }
  }
  return { routes, errors };
}
